import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/lib/store";
import { useUrlProject } from "@/hooks/useUrlProject";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Home,
  Plus,
  Package,
  Download,
  FileText,
  FolderOpen,
  MoreHorizontal,
  Users,
  Calendar,
  Archive,
  Mail,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Send,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  FileSpreadsheet,
  Share2,
  Copy,
  AlertTriangle,
  Lock,
  Globe,
  Circle,
  Loader2,
  Settings,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { TenderSubmission } from "../../../shared/types";
import { Pagination } from "@/components/ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

interface TenderPackage {
  id: string;
  name: string;
  description?: string;
  versionType: "current" | "fixed";
  status: "active" | "archived";
  submissionStatus: "draft" | "not_sent" | "sent" | "viewed" | "downloaded" | "tender_returned" | "won" | "lost" | "expired";
  totalFiles: number;
  totalSize: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy?: string;
  projectId?: string;
  packageNumber?: string;
  sentAt?: string;
  submissionDeadline?: string;
  recipientName?: string;
  recipientCompany?: string;
  parentPackageId?: string;
  isAddendum: boolean;
  addendums?: TenderPackage[];
  isLocked?: boolean; // Added for read-only status
  // Company status columns (numbers for counts, boolean for awarded)
  companySent?: number;
  companyViewed?: number;
  companyDownloaded?: number;
  companyReturned?: number;
  companyAwarded?: boolean;
  // ACC IDs
  accAccountId?: string;
  accHubId?: string;
}

// TenderSubmission type is now imported from shared schema

interface CreatePackageData {
  name: string;
  description?: string;
  versionType: "current" | "fixed";
}

import { STATUS_CONFIG } from '../lib/statusColors';

const submissionStatusConfig = {
  draft: { label: "Draft", color: STATUS_CONFIG.draft.color, icon: FileText },
  not_sent: { label: "Draft", color: STATUS_CONFIG.not_sent.color, icon: FileText },
  sent: { label: "Sent", color: STATUS_CONFIG.sent.color, icon: Send },
  viewed: { label: "Viewed", color: STATUS_CONFIG.viewed.color, icon: Eye },
  downloaded: { label: "Downloaded", color: STATUS_CONFIG.downloaded.color, icon: Download },
  tender_returned: { label: "Returned", color: STATUS_CONFIG.tender_returned.color, icon: CheckCircle },
  awarded: { label: "Awarded", color: STATUS_CONFIG.awarded.color, icon: CheckCircle },
  awarded_and_closed: { label: "Awarded", color: STATUS_CONFIG.awarded_and_closed.color, icon: CheckCircle },
  won: { label: "Won", color: STATUS_CONFIG.won.color, icon: CheckCircle },
  lost: { label: "Lost", color: STATUS_CONFIG.lost.color, icon: XCircle },
  rejected: { label: "Rejected", color: STATUS_CONFIG.rejected.color, icon: XCircle },
  expired: { label: "Expired", color: STATUS_CONFIG.expired.color, icon: Clock },
};

type SortField = 'id' | 'name' | 'status' | 'sentAt' | 'deadline' | 'totalFiles' | 'createdAt' | 'createdBy';
type SortDirection = 'asc' | 'desc';

// Component to fetch and display next package number
function NextPackageNumber({ projectId }: { projectId?: string }) {
  const { data: nextNumber, isLoading } = useQuery({
    queryKey: ['next-package-number', projectId],
    queryFn: async () => {
      const url = projectId 
        ? `/api/tender-packages/next-package-number?projectId=${projectId}`
        : '/api/tender-packages/next-package-number';
      const data = await apiRequest(url) as { nextPackageNumber: string };
      return data.nextPackageNumber;
    }
  });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  return <span className="font-semibold">{nextNumber || 'WP0001'}</span>;
}

export default function TenderPackages() {
  const [location, setLocation] = useLocation();
  const { getPageHeaderClass } = useSidebarLayout();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [createdByFilter, setCreatedByFilter] = useState<string>("all");
  const [hasAddendumFilter, setHasAddendumFilter] = useState<string>("all");
  const [dateRangeFilter, setDateRangeFilter] = useState<string>("all");
  const [deadlineFilter, setDeadlineFilter] = useState<string>("all");
  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharePackageId, setSharePackageId] = useState<string>("");
  const [shareUrl, setShareUrl] = useState<string>("");
  const [viewMode, setViewMode] = useState<'expand_all' | 'main_pack_only'>('expand_all');
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    package: true,
    status: true,
    files: true,
    type: true,
    createdAt: true,
    createdBy: false,
    sentAt: false,
    deadline: true,
    companySent: true,
    companyViewed: true,
    companyDownloaded: false,
    companyReturned: true,
    companyAwarded: false,
  });
  const [newPackage, setNewPackage] = useState<CreatePackageData>({
    name: "",
    description: "",
    versionType: "fixed",
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { selectedProject } = useAppStore();
  const { urlProjectId } = useUrlProject();
  
  // Use URL project ID or selected project ID for filtering
  // Ensure we use the full Autodesk project ID format (with b. prefix)
  const projectId = urlProjectId || selectedProject?.id;

  // Handle URL search parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.split('?')[1] || '');
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setNameFilter(searchParam);
    }
  }, [location]);
  
  const { data: packages = [], isLoading, error, refetch } = useQuery<TenderPackage[]>({
    queryKey: ["tender-packages", projectId],
    queryFn: () => apiRequest(`/api/tender-packages${projectId ? `?projectId=${projectId}` : ''}`),
    enabled: !!projectId,
    retry: 3,
    retryDelay: 1000,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });

  // Fetch submissions data to show in combined view
  const { data: submissions = [] } = useQuery<TenderSubmission[]>({
    queryKey: ['tender-submissions', projectId],
    queryFn: () => apiRequest(`/api/tender-submissions${projectId ? `?projectId=${projectId}` : ''}`),
    enabled: !!projectId,
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });

  // Auto-expand packages that have addendums
  useEffect(() => {
    if (packages.length > 0) {
      const newExpanded = new Set<string>();
      packages.forEach(pkg => {
        if (pkg.addendums && pkg.addendums.length > 0) {
          newExpanded.add(pkg.id);
        }
      });
      setExpandedPackages(newExpanded);
    }
  }, [packages.length]); // Only depend on packages.length to prevent infinite loop

  // Handle view mode changes
  useEffect(() => {
    if (viewMode === 'main_pack_only') {
      // Collapse all packages to hide addendums
      setExpandedPackages(new Set());
    } else {
      // Expand all packages with addendums
      const newExpanded = new Set<string>();
      packages.forEach(pkg => {
        if (pkg.addendums && pkg.addendums.length > 0) {
          newExpanded.add(pkg.id);
        }
      });
      setExpandedPackages(newExpanded);
    }
    
    // Reset to first page when view mode changes
    setCurrentPage(1);
  }, [viewMode]); // Remove packages dependency to prevent infinite loop

  // Refresh data when page becomes visible (e.g., when returning from package detail)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page is now visible, refresh data
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetch]);

  // Get unique creators for filter dropdown (including main packages and addendums)
  const allCreators: string[] = [];
  packages.forEach(pkg => {
    if (pkg.createdBy) {
      allCreators.push(pkg.createdBy);
    }
    // Also include creators from addendums (sub-packages)
    if (pkg.addendums && pkg.addendums.length > 0) {
      pkg.addendums.forEach(addendum => {
        if (addendum.createdBy) {
          allCreators.push(addendum.createdBy);
        }
      });
    }
  });
  const uniqueCreators = Array.from(new Set(allCreators)).filter(Boolean);

  // Sorting function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column headers
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-300" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-yellow-400" />
      : <ArrowDown className="w-4 h-4 text-yellow-400" />;
  };

  // Handle item selection
  const handleItemSelect = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>();
      // Add all main packages
      paginatedPackages.forEach(pkg => {
        allIds.add(pkg.id);
        // Add all addendums for each package
        if (pkg.addendums && pkg.addendums.length > 0) {
          pkg.addendums.forEach(addendum => {
            allIds.add(addendum.id);
          });
        }
      });
      setSelectedItems(allIds);
    } else {
      setSelectedItems(new Set());
    }
    setSelectAll(checked);
  };


  // Comprehensive filtering logic
  const filteredPackages = packages.filter(pkg => {
    // Status filter
    if (statusFilter !== "all") {
      const matchesMainStatus = pkg.submissionStatus === statusFilter;
      const matchesAddendumStatus = pkg.addendums && pkg.addendums.length > 0 
        ? pkg.addendums.some(addendum => addendum.submissionStatus === statusFilter)
        : false;
      
      if (!matchesMainStatus && !matchesAddendumStatus) return false;
    }

    // Name filter (case-insensitive search)
    if (nameFilter.trim() !== "") {
      const searchTerm = nameFilter.toLowerCase();
      const matchesMainName = pkg.name.toLowerCase().includes(searchTerm);
      const matchesAddendumName = pkg.addendums && pkg.addendums.length > 0
        ? pkg.addendums.some(addendum => addendum.name.toLowerCase().includes(searchTerm))
        : false;
      if (!matchesMainName && !matchesAddendumName) return false;
    }

    // Created by filter (check both main package and addendums)
    if (createdByFilter !== "all") {
      const matchesMainCreator = pkg.createdBy === createdByFilter;
      const matchesAddendumCreator = pkg.addendums && pkg.addendums.length > 0
        ? pkg.addendums.some(addendum => addendum.createdBy === createdByFilter)
        : false;
      
      if (!matchesMainCreator && !matchesAddendumCreator) return false;
    }

    // Has addendum filter
    if (hasAddendumFilter !== "all") {
      const hasAddendums = pkg.addendums && pkg.addendums.length > 0;
      if (hasAddendumFilter === "yes" && !hasAddendums) return false;
      if (hasAddendumFilter === "no" && hasAddendums) return false;
    }

    // Date range filter (created at)
    if (dateRangeFilter !== "all") {
      const createdDate = new Date(pkg.createdAt);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (dateRangeFilter) {
        case "today":
          if (daysDiff > 0) return false;
          break;
        case "week":
          if (daysDiff > 7) return false;
          break;
        case "month":
          if (daysDiff > 30) return false;
          break;
        case "quarter":
          if (daysDiff > 90) return false;
          break;
      }
    }

    // Deadline filter
    if (deadlineFilter !== "all" && pkg.submissionDeadline) {
      const deadline = new Date(pkg.submissionDeadline);
      const now = new Date();
      const daysDiff = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (deadlineFilter) {
        case "overdue":
          if (daysDiff >= 0) return false;
          break;
        case "due_today":
          if (daysDiff !== 0) return false;
          break;
        case "due_week":
          if (daysDiff < 0 || daysDiff > 7) return false;
          break;
        case "due_month":
          if (daysDiff < 0 || daysDiff > 30) return false;
          break;
      }
    }

    return true;
  }).sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'id':
        aValue = a.packageNumber || a.id;
        bValue = b.packageNumber || b.id;
        break;
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'status':
        aValue = a.submissionStatus;
        bValue = b.submissionStatus;
        break;
      case 'sentAt':
        aValue = a.sentAt ? new Date(a.sentAt).getTime() : 0;
        bValue = b.sentAt ? new Date(b.sentAt).getTime() : 0;
        break;
      case 'deadline':
        aValue = a.submissionDeadline ? new Date(a.submissionDeadline).getTime() : 0;
        bValue = b.submissionDeadline ? new Date(b.submissionDeadline).getTime() : 0;
        break;
      case 'totalFiles':
        aValue = a.totalFiles || 0;
        bValue = b.totalFiles || 0;
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      case 'createdBy':
        aValue = a.createdBy.toLowerCase();
        bValue = b.createdBy.toLowerCase();
        break;
      default:
        aValue = a.createdAt;
        bValue = b.createdAt;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic - different counting based on view mode
  // In Expand All mode, we need to count all packages including addendums
  // In Main Pack Only mode, we only count main packages
  const allPackagesWithAddendums = packages.flatMap(pkg => {
    const addendums = pkg.addendums || [];
    return [pkg, ...addendums];
  });
  
  const totalItems = viewMode === 'main_pack_only' 
    ? filteredPackages.filter(pkg => !pkg.isAddendum).length  // Only count main packages
    : allPackagesWithAddendums.length;  // Count all packages including addendums
  
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // For pagination, we need to slice the appropriate packages based on view mode
  const packagesForPagination = viewMode === 'main_pack_only' 
    ? filteredPackages.filter(pkg => !pkg.isAddendum)  // Only main packages
    : filteredPackages;  // All main packages, addendums will be shown under their parent packages
  
  const paginatedPackages = packagesForPagination.slice(startIndex, endIndex);

  // Update select all state when items change
  useEffect(() => {
    const allIds = new Set<string>();
    // Add all main packages
    paginatedPackages.forEach(pkg => {
      allIds.add(pkg.id);
      // Add all addendums for each package
      if (pkg.addendums && pkg.addendums.length > 0) {
        pkg.addendums.forEach(addendum => {
          allIds.add(addendum.id);
        });
      }
    });
    const isAllSelected = allIds.size > 0 && Array.from(allIds).every(id => selectedItems.has(id));
    setSelectAll(isAllSelected);
  }, [selectedItems, paginatedPackages]);

  const togglePackageExpansion = (packageId: string) => {
    const newExpanded = new Set(expandedPackages);
    if (newExpanded.has(packageId)) {
      newExpanded.delete(packageId);
    } else {
      newExpanded.add(packageId);
    }
    setExpandedPackages(newExpanded);
  };

  const createPackageMutation = useMutation({
    mutationFn: (packageData: CreatePackageData) => {
      if (!projectId) {
        throw new Error("Please select a project first");
      }
      return apiRequest(`/api/tender-packages?projectId=${projectId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(packageData),
      });
    },
    onSuccess: () => {
      // Invalidate all tender-packages queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["tender-packages"] });
      // Also invalidate project-specific queries
      queryClient.invalidateQueries({ queryKey: ["tender-packages", projectId] });
      // Invalidate next package number query to refresh the suggested number
      queryClient.invalidateQueries({ queryKey: ["next-package-number", projectId] });
      // Invalidate dashboard analytics queries - match exact query keys
      queryClient.invalidateQueries({ queryKey: ["/api/tender-submissions", projectId] });
      queryClient.invalidateQueries({ queryKey: ["/api/tender-packages", projectId] });
      queryClient.invalidateQueries({ queryKey: ["/api/package-analytics", projectId] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities", projectId] });
      // Also invalidate any general queries without project ID
      queryClient.invalidateQueries({ queryKey: ["tender-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["package-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      
      setIsCreateDialogOpen(false);
      setNewPackage({ name: "", description: "", versionType: "current" });
      toast({ title: "Package created successfully" });
    },
    onError: () => {
      toast({ 
        title: "Error creating package",
        description: "Please try again later",
        variant: "destructive"
      });
    },
  });

  const downloadPackageMutation = useMutation<{ blob: Blob; fileName: string; size: number; formattedSize: string }, Error, string>({
    mutationFn: async (packageId: string) => {
      const packageData = packages?.find(pkg => pkg.id === packageId);
      const packageName = packageData?.name || packageData?.packageNumber || 'Package';
      
      // Show download started notification
      toast({ 
        title: "Download started", 
        description: `Downloading ${packageName}... This may take a while for large packages.`,
        duration: 3000
      });
      
      const response = await fetch(`/api/tender-packages/${packageId}/download`, {
        method: "POST",
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
            // Try to extract filename from Content-Disposition header
      let fileName = '';
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        // Try multiple patterns to extract filename
        // Pattern 1: filename="value" or filename='value'
        let fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        } else {
          // Pattern 2: filename*=UTF-8''value (RFC 5987)
          fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          } else {
            // Pattern 3: filename*=value
            fileNameMatch = contentDisposition.match(/filename\*=([^;]+)/i);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1];
            }
          }
        }

        // Decode URI-encoded filename if needed
        if (fileName) {
          try {
            fileName = decodeURIComponent(fileName);
          } catch (e) {
            // If decoding fails, try to decode with replacement
            try {
              fileName = decodeURIComponent(fileName.replace(/%(?![0-9A-F]{2})/gi, '%25'));
            } catch (e2) {
              // If still fails, use the original filename
            }
          }
        }
      }

      // Fallback to package name if header doesn't have filename or filename is empty/invalid
      if (!fileName || fileName.trim() === '' || fileName === 'null' || fileName === 'undefined') {
        const packageData = packages?.find(pkg => pkg.id === packageId);
        if (packageData?.name && String(packageData.name).trim()) {
          fileName = `${String(packageData.name).trim().replace(/[^a-zA-Z0-9\u4e00-\u9fa5_-]/g, '_')}.zip`;
        } else if (packageData?.packageNumber && String(packageData.packageNumber).trim()) {
          fileName = `${String(packageData.packageNumber).trim().replace(/[^a-zA-Z0-9\u4e00-\u9fa5_-]/g, '_')}.zip`;
        } else {
          // Last resort: use a truncated version of the UUID for better readability
          const shortId = packageId.substring(0, 8);
          fileName = `package_${shortId}.zip`;
        }
      }
      
      const blob = await response.blob();
      
      // Verify blob size
      const blobSize = blob.size;
      const contentLength = response.headers.get('Content-Length');
      const expectedSize = contentLength ? parseInt(contentLength, 10) : null;
      
      if (expectedSize && blobSize !== expectedSize) {
        console.warn(`[DOWNLOAD] Size mismatch: expected ${expectedSize} bytes, got ${blobSize} bytes`);
        // Still allow download but warn user
      }
      
      // Format file size for display
      const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
      };
      
      return { blob, fileName, size: blobSize, formattedSize: formatFileSize(blobSize) };
    },
    onSuccess: (result: { blob: Blob; fileName: string; size: number; formattedSize: string }, packageId: string) => {
      // Create download link using filename from response header or fallback
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      // Show detailed success message with file size
      const packageData = packages?.find(pkg => pkg.id === packageId);
      const packageName = packageData?.name || packageData?.packageNumber || 'Package';
      
      toast({ 
        title: "Download completed", 
        description: `${packageName} (${result.formattedSize}) downloaded successfully. Please verify the file integrity.`,
        duration: 5000
      });
    },
    onError: (error: Error, packageId: string) => {
      const packageData = packages?.find(pkg => pkg.id === packageId);
      const packageName = packageData?.name || packageData?.packageNumber || 'Package';
      
      toast({ 
        title: "Download failed",
        description: `Failed to download ${packageName}. ${error.message || 'Please check your connection and try again.'}`,
        variant: "destructive",
        duration: 5000
      });
    },
  });

  // Export file register mutation
  const exportFileRegisterMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await fetch(`/api/tender-packages/${packageId}/export-file-register`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export file register');
      }
      
      return { blob: await response.blob(), packageId };
    },
    onSuccess: ({ blob, packageId }) => {
      const pkg = packages.find(p => p.id === packageId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pkg?.name || 'Package'}_File_Register.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "✅ File Register Exported",
        description: "Excel file has been downloaded successfully",
      });
    },
  });

  // Auto-update mutation
  const autoUpdateMutation = useMutation({
    mutationFn: (packageId: string) => apiRequest(`/api/tender-packages/${packageId}/auto-update`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });
      toast({
        title: "Auto-update completed",
        description: "All outdated files updated to latest versions",
      });
    },
  });

  // Share package mutation
  const sharePackageMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await fetch(`/api/tender-packages/${packageId}/share-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ shareType: 'download', versionType: 'current' })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      setShareUrl(data.downloadUrl);
      setShareDialogOpen(true);
    },
  });

  const handleShareLink = (packageId: string) => {
    setSharePackageId(packageId);
    sharePackageMutation.mutate(packageId);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "✅ Link Copied",
        description: "Download link copied to clipboard successfully.",
      });
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy link to clipboard.",
        variant: "destructive"
      });
    }
  };

  const handleCreatePackage = () => {
    if (!selectedProject?.id) {
      toast({ 
        title: "Please select a project first",
        variant: "destructive"
      });
      return;
    }
    
    if (!newPackage.name.trim()) {
      toast({ 
        title: "Package name required",
        variant: "destructive"
      });
      return;
    }
    createPackageMutation.mutate(newPackage);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Helper function to calculate total companies for a package
  const getTotalCompanies = (pkg: TenderPackage) => {
    // Use companySent as the total since all companies that received the package are counted here
    return pkg.companySent || 0;
  };

  // Helper function to format company status display
  const formatCompanyStatus = (count: number, total: number) => {
    if (total === 0) return '0/0';
    return `${count}/${total}`;
  };

  // Export to CSV function
  const exportToCSV = () => {
    setIsExporting(true);
    
    try {
      // Get all packages including addendums based on view mode
      const packagesToExport = viewMode === 'main_pack_only' 
        ? filteredPackages.filter(pkg => !pkg.isAddendum)
        : filteredPackages.flatMap(pkg => {
            const addendums = pkg.addendums || [];
            return [pkg, ...addendums];
          });

      // Define all headers (including hidden columns)
      const headers = [
        'Package #',
        'Package Name', 
        'Status',
        'Package Files',
        'Version Type',
        'Created At',
        'Created By',
        'Submission Date',
        'Due Date',
        'Company Sent',
        'Company Viewed',
        'Company Downloaded',
        'Company Returned',
        'Company Awarded'
      ];

      // Create CSV data
      const csvData = packagesToExport.map(pkg => {
        const statusConfig = submissionStatusConfig[pkg.submissionStatus] || { label: pkg.submissionStatus };
        
        return {
          'Package #': pkg.packageNumber || `#${pkg.id.slice(-4)}`,
          'Package Name': pkg.name,
          'Status': statusConfig.label,
          'Package Files': pkg.totalFiles || 0,
          'Version Type': pkg.versionType === 'fixed' ? 'Fixed' : 'Current',
          'Created At': pkg.createdAt ? new Date(pkg.createdAt).toISOString().split('T')[0] : '',
          'Created By': pkg.createdBy || '',
          'Submission Date': pkg.sentAt ? new Date(pkg.sentAt).toISOString().split('T')[0] : '',
          'Due Date': pkg.submissionDeadline ? new Date(pkg.submissionDeadline).toISOString().split('T')[0] : '',
          'Company Sent': (pkg.companySent || 0).toString(),
          'Company Viewed': (pkg.companyViewed || 0).toString(),
          'Company Downloaded': (pkg.companyDownloaded || 0).toString(),
          'Company Returned': (pkg.companyReturned || 0).toString(),
          'Company Awarded': pkg.companyAwarded ? 'Yes' : 'No'
        };
      });

      // Convert to CSV format
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tender-packages-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "✅ Export Successful",
        description: `Exported ${packagesToExport.length} packages to CSV`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data to CSV",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <main className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Tender Packages & Submissions</h1>
            <p className="text-gray-600 mt-1">
              Create and manage document packages for tender submissions with tracking
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    );
  }

  return (
    <div className="h-screen flex flex-col">

      {/* Scrollable Content with top padding to account for global header (64px = pt-16) */}
      <main className="flex-1 flex flex-col p-6 pt-20 overflow-hidden">
        {error ? (
          <Card className="text-center py-12">
            <CardContent>
              <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading packages</h3>
              <p className="text-gray-500 mb-6">
                {error.message || 'Failed to load packages. Please try again.'}
              </p>
              <Button
                onClick={() => refetch()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : packages && packages.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No packages yet</h3>
              <p className="text-gray-500 mb-6">
                Create your first package to get started with tender submissions
              </p>
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Package
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="h-full flex flex-col">
            {/* Fixed Header Card */}
            <Card className="flex-shrink-0 mb-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                 <div className="flex items-center gap-4">
                   <CardTitle className="flex items-center gap-2 text-lg">
                     <span>Tender Packages</span>
                   </CardTitle>
                   
                   {/* Batch Actions - only show when items are selected */}
                   {selectedItems.size > 0 && (
                     <DropdownMenu open={isActionsDropdownOpen} onOpenChange={setIsActionsDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button 
                           variant="default" 
                           size="sm" 
                           className="h-8 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 relative z-[9999]"
                         >
                           <MoreHorizontal className="w-4 h-4 mr-2" />
                           Actions ({selectedItems.size})
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent align="end" className="w-56 z-[9999]">
                         <DropdownMenuItem 
                           onClick={(e) => {
                             e.preventDefault();
                             // Download selected packages
                             selectedItems.forEach(packageId => {
                               downloadPackageMutation.mutate(packageId);
                             });
                             // Keep dropdown open during download
                             setIsActionsDropdownOpen(true);
                           }}
                           disabled={downloadPackageMutation.isPending}
                           className="cursor-pointer"
                         >
                           {downloadPackageMutation.isPending ? (
                             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           ) : (
                             <Download className="mr-2 h-4 w-4" />
                           )}
                           Download Selected
                         </DropdownMenuItem>
                         <DropdownMenuItem 
                           onClick={() => {
                             // Export file register for selected packages
                             selectedItems.forEach(packageId => {
                               exportFileRegisterMutation.mutate(packageId);
                             });
                           }}
                           disabled={exportFileRegisterMutation.isPending}
                           className="cursor-pointer"
                         >
                           <FileSpreadsheet className="mr-2 h-4 w-4" />
                           Export File Register
                         </DropdownMenuItem>
                         <DropdownMenuItem 
                           onClick={() => {
                             // Auto-update selected packages
                             selectedItems.forEach(packageId => {
                               autoUpdateMutation.mutate(packageId);
                             });
                           }}
                           disabled={autoUpdateMutation.isPending}
                           className="cursor-pointer"
                         >
                           <RefreshCw className="mr-2 h-4 w-4" />
                           Auto-Update to Latest
                         </DropdownMenuItem>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   )}
                 </div>
                <div className="flex items-center gap-2">
                  {/* Table Settings Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 border-gray-300 hover:border-gray-400 hover:bg-gray-50">
                        <Settings className="w-4 h-4 mr-2" />
                        Table Settings
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-0 z-[10001]">
                      <div className="p-4">
                        <h4 className="font-semibold text-sm mb-4 text-gray-900">Table Settings</h4>
                        
                        {/* View Mode Toggle */}
                        <div className="mb-6">
                          <label className="text-sm font-semibold mb-3 block text-gray-800">View Mode</label>
                          <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
                            <Button
                              variant={viewMode === 'expand_all' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setViewMode('expand_all')}
                              className={`h-8 px-3 text-xs font-medium transition-all ${
                                viewMode === 'expand_all' 
                                  ? 'bg-white shadow-sm text-gray-900' 
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              All Packages
                            </Button>
                            <Button
                              variant={viewMode === 'main_pack_only' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setViewMode('main_pack_only')}
                              className={`h-8 px-3 text-xs font-medium transition-all ${
                                viewMode === 'main_pack_only' 
                                  ? 'bg-white shadow-sm text-gray-900' 
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              Main Only
                            </Button>
                          </div>
                        </div>
                        
                        {/* Column Visibility */}
                        <div className="mb-6">
                          <label className="text-sm font-semibold mb-3 block text-gray-800">Column Visibility</label>
                          <div className="max-h-48 overflow-y-auto pr-2">
                            <div className="space-y-2">
                              {Object.entries(visibleColumns).map(([key, value]) => (
                                <div key={key} className="flex items-center py-1.5">
                                  <Checkbox
                                    id={key}
                                    checked={value}
                                    onCheckedChange={(checked) => 
                                      setVisibleColumns(prev => ({ ...prev, [key]: checked }))
                                    }
                                    className="mr-3 flex-shrink-0"
                                  />
                                  <label 
                                    htmlFor={key} 
                                    className="text-sm text-gray-700 cursor-pointer flex-1"
                                  >
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Export CSV */}
                        <div className="border-t border-gray-200 pt-4">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={exportToCSV}
                            disabled={isExporting || filteredPackages.length === 0}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium h-9 shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            {isExporting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Exporting...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Export CSV
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Filter Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                    className="text-gray-600 hover:text-gray-900 h-8 border-gray-300 hover:border-gray-400 bg-transparent hover:bg-gray-50"
                  >
                    {isFilterExpanded ? (
                      <>
                        <ChevronUp className="w-3 h-3 mr-1" />
                        Hide Filters
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3 mr-1" />
                        Show Filters
                      </>
                    )}
                  </Button>
                  
                  {/* Create Package Button */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-2 h-10 text-sm">
                        <Plus className="h-4 w-4 mr-1" />
                  Create Package
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] bg-white border border-gray-300 shadow-2xl" aria-describedby="create-package-description">
                <DialogHeader className="pb-4 border-b border-gray-200">
                  <DialogTitle className="flex items-center text-xl font-semibold text-gray-900">
                    <Package className="w-6 h-6 mr-3 text-blue-600" />
                    Create New Package
                  </DialogTitle>
                  <DialogDescription id="create-package-description" className="text-gray-700 mt-2 text-base">
                    Create a new document package for organizing tender files
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-6">
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-800">Package Number</label>
                    <div className="h-11 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 flex items-center text-gray-700 font-mono">
                      <NextPackageNumber projectId={projectId} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-800">Package Name</label>
                    <Input
                      value={newPackage.name}
                      onChange={(e) =>
                        setNewPackage({ ...newPackage, name: e.target.value })
                      }
                      placeholder="Enter package name"
                      className="h-11 text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-800">Description</label>
                    <Textarea
                      value={newPackage.description}
                      onChange={(e) =>
                        setNewPackage({ ...newPackage, description: e.target.value })
                      }
                      placeholder="Optional description"
                      rows={3}
                      className="resize-none text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-800">Version Type</label>
                           <div className="flex gap-2">
                             <button
                               type="button"
                               onClick={() => setNewPackage({ ...newPackage, versionType: "fixed" })}
                               className={`px-4 py-2 text-sm font-medium rounded-md border-2 transition-colors ${
                                 newPackage.versionType === "fixed"
                                   ? "border-blue-500 text-blue-600 bg-blue-50"
                                   : "border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50"
                               }`}
                             >
                               Fixed Version
                             </button>
                             <button
                               type="button"
                               onClick={() => setNewPackage({ ...newPackage, versionType: "current" })}
                               className={`px-4 py-2 text-sm font-medium rounded-md border-2 transition-colors ${
                                 newPackage.versionType === "current"
                                   ? "border-blue-500 text-blue-600 bg-blue-50"
                                   : "border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50"
                               }`}
                             >
                               Current Version
                             </button>
                           </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><span className="font-medium">Fixed Version:</span> Uses file versions selected when adding files to package</div>
                      <div><span className="font-medium">Current Version:</span> Auto-updates to latest file versions until package is locked</div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreatePackage}
                    disabled={createPackageMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {createPackageMutation.isPending ? "Creating..." : "Create Package"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
        </div>
              </div>
            </CardHeader>
            {isFilterExpanded && (
              <CardContent className="pt-0 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Search Name</label>
                      <Input
                        placeholder="Search package names..."
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="viewed">Viewed</SelectItem>
                          <SelectItem value="downloaded">Downloaded</SelectItem>
                          <SelectItem value="tender_returned">Returned</SelectItem>
                          <SelectItem value="awarded">Awarded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Created By</label>
                      <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All creators" />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="all">All Creators</SelectItem>
                          {uniqueCreators.map(creator => (
                            <SelectItem key={creator} value={creator}>{creator}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Date Range</label>
                      <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All dates" />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">This Week</SelectItem>
                          <SelectItem value="month">This Month</SelectItem>
                          <SelectItem value="quarter">This Quarter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Has Addendum</label>
                      <Select value={hasAddendumFilter} onValueChange={setHasAddendumFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All packages" />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="all">All Packages</SelectItem>
                          <SelectItem value="yes">Has Addendum</SelectItem>
                          <SelectItem value="no">No Addendum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Deadline</label>
                      <Select value={deadlineFilter} onValueChange={setDeadlineFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All deadlines" />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="all">All Deadlines</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                          <SelectItem value="due_today">Due Today</SelectItem>
                          <SelectItem value="due_week">Due This Week</SelectItem>
                          <SelectItem value="due_month">Due This Month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 flex items-end">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setStatusFilter("all");
                          setNameFilter("");
                          setCreatedByFilter("all");
                          setHasAddendumFilter("all");
                          setDateRangeFilter("all");
                          setDeadlineFilter("all");
                        }}
                        className="w-full"
                      >
                        Clear All Filters
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}

            </Card>
            
            {/* Fixed Table Header */}
            <Card className="flex-shrink-0">
              <CardContent className="p-0">
                <div 
                  id="table-header-scroll"
                  className="overflow-x-auto table-header-no-scrollbar"
                  onScroll={(e) => {
                    const bodyScroll = document.getElementById('table-body-scroll');
                    if (bodyScroll) {
                      bodyScroll.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                >
                    <div className="min-w-full" style={{ minWidth: '1600px' }}>
                      <Table className="w-full tender-packages-table">
                      <TableHeader className="bg-gray-700">
                  <TableRow className="border-b border-gray-200">
                              {/* Selection column */}
                              <TableHead 
                                className="text-white font-medium text-center sticky left-0 bg-gray-700"
                                style={{ backgroundColor: '#374151', opacity: 1, width: '32px', minWidth: '32px', maxWidth: '32px', zIndex: 100 }}
                              >
                                <Checkbox
                                  checked={selectAll}
                                  onCheckedChange={handleSelectAll}
                                  className="border-white data-[state=checked]:bg-white data-[state=checked]:text-gray-800 rounded-none h-3 w-3"
                                  style={{ verticalAlign: 'middle' }}
                                />
                              </TableHead>
                              {visibleColumns.package && (
                    <TableHead 
                                  className="text-white font-medium cursor-pointer hover:bg-gray-700 sticky left-[32px] bg-gray-700"
                                  style={{ backgroundColor: '#374151', opacity: 1, width: '140px', minWidth: '140px', maxWidth: '140px', zIndex: 99, position: 'sticky' }}
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1 ml-2">
                                    Package #
                        {getSortIcon('name')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.package && (
                     <TableHead 
                                   className="w-[160px] text-white font-medium cursor-pointer hover:bg-gray-700 sticky left-[172px] bg-gray-700"
                                   style={{ backgroundColor: '#374151', opacity: 1, width: '160px', minWidth: '160px', maxWidth: '160px', zIndex: 98, position: 'sticky' }}
                                   onClick={() => handleSort('name')}
                                >
                                  <div className="flex items-center gap-1">
                                    Package Name
                                    {getSortIcon('name')}
                                  </div>
                                </TableHead>
                              )}
                              {visibleColumns.status && (
                                <TableHead 
                                  className="w-[120px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 sticky left-[332px] bg-gray-700 sticky-left-column"
                                  style={{ backgroundColor: '#374151', opacity: 1, width: '120px', minWidth: '120px', maxWidth: '120px', zIndex: 97, position: 'sticky' }}
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.files && (
                    <TableHead 
                                  className="w-[100px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 bg-gray-700"
                      onClick={() => handleSort('totalFiles')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Package Files
                        {getSortIcon('totalFiles')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.type && (
                                <TableHead className="w-[120px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                                    Version Type
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.createdAt && (
                    <TableHead 
                                  className="w-[150px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 bg-gray-700"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Created At
                        {getSortIcon('createdAt')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.createdBy && (
                    <TableHead 
                                  className="w-[150px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 bg-gray-700"
                      onClick={() => handleSort('createdBy')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Created By
                        {getSortIcon('createdBy')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.sentAt && (
                    <TableHead 
                                  className="w-[120px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 bg-gray-700"
                      onClick={() => handleSort('sentAt')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Submission Date
                        {getSortIcon('sentAt')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.deadline && (
                    <TableHead 
                                  className="w-[120px] text-white font-medium text-center cursor-pointer hover:bg-gray-700 bg-gray-700"
                      onClick={() => handleSort('deadline')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Due Date
                        {getSortIcon('deadline')}
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.companySent && (
                                <TableHead className="w-[140px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        Company Sent
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.companyViewed && (
                                <TableHead className="w-[140px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        Company Viewed
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.companyDownloaded && (
                                <TableHead className="w-[140px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        Company Downloaded
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.companyReturned && (
                                <TableHead className="w-[150px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        Company Returned
                      </div>
                    </TableHead>
                              )}
                              {visibleColumns.companyAwarded && (
                                <TableHead className="w-[150px] text-white font-medium text-center bg-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        Company Awarded
                      </div>
                    </TableHead>
                              )}
                  </TableRow>
                </TableHeader>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Scrollable Table Body */}
              <Card className="flex-1 flex flex-col border-t-0 shadow-none">
                <CardContent className="p-0 flex-1 flex flex-col m-0 overflow-hidden" style={{ padding: 0 }}>
                  <div className="flex-1 flex flex-col" style={{ 
                    minHeight: '150px',
                    maxHeight: 'calc(100vh - 280px)'
                  }}>
                    <div 
                      id="table-body-scroll"
                      className="flex-1 overflow-auto table-scrollbar"
                      style={{
                        scrollbarWidth: 'auto',
                        scrollbarColor: '#9ca3af #f8fafc'
                      }}
                      onScroll={(e) => {
                        const headerScroll = document.getElementById('table-header-scroll');
                        if (headerScroll) {
                          headerScroll.scrollLeft = e.currentTarget.scrollLeft;
                        }
                      }}
                    >
                      <div className="min-w-full" style={{ minWidth: '1600px' }}>
                        <Table className="w-full border-collapse m-0 tender-packages-table">
                          <TableBody>
                            {paginatedPackages.map((pkg) => {
                      const isExpanded = expandedPackages.has(pkg.id);
                      const addendums = pkg.addendums || []; // Use nested addendum data from server
                      const hasAddendums = addendums.length > 0;
                      
                      return (
                        <React.Fragment key={pkg.id}>
                          {/* Main Package Row */}
                          <TableRow 
                            className="cursor-pointer hover:bg-gray-50/50 border-b border-gray-200 transition-colors"
                            onClick={() => {
                              // Navigate to package detail page with proper project context
                              setLocation(`/tender-packages/projects/${projectId}/${pkg.id}`);
                            }}
                          >
                            {/* Selection checkbox */}
                            <TableCell className="w-[32px] sticky left-0 bg-white text-center" style={{ backgroundColor: '#ffffff', opacity: 1, width: '32px', minWidth: '32px', maxWidth: '32px', zIndex: 100, position: 'sticky' }}>
                              <Checkbox
                                checked={selectedItems.has(pkg.id)}
                                onCheckedChange={checked => handleItemSelect(pkg.id, checked as boolean)}
                                onClick={e => e.stopPropagation()}
                                className="rounded-none h-3 w-3"
                                style={{ verticalAlign: 'middle' }}
                              />
                            </TableCell>
                            {visibleColumns.package && (
                              <>
                                <TableCell className="w-[140px] sticky left-[32px] bg-white" style={{ backgroundColor: '#ffffff', opacity: 1, width: '140px', minWidth: '140px', maxWidth: '140px', zIndex: 99, position: 'sticky' }}>
                                  <div className="flex items-center space-x-2">
                                    {hasAddendums ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="p-0 h-5 w-5 rounded-md bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-all duration-200 hover:scale-105"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const newExpanded = new Set(expandedPackages);
                                      if (isExpanded) {
                                        newExpanded.delete(pkg.id);
                                      } else {
                                        newExpanded.add(pkg.id);
                                      }
                                      setExpandedPackages(newExpanded);
                                    }}
                                  >
                                    {isExpanded ? 
                                      <ChevronDown className="h-3 w-3 text-blue-600" /> : 
                                      <ChevronRight className="h-3 w-3 text-blue-600" />
                                    }
                                  </Button>
                                    ) : (
                                      <div className="w-5 h-5"></div>
                                )}
                                <Package className="h-4 w-4 text-blue-600" />
                                  <div className="font-medium text-gray-900 truncate">
                                      {pkg.packageNumber || `#${pkg.id.slice(-4)}`}
                                    </div>
                                  </div>
                                </TableCell>
                                 <TableCell className="w-[160px] sticky left-[172px] bg-white" style={{ backgroundColor: '#ffffff', opacity: 1, width: '160px', minWidth: '160px', maxWidth: '160px', zIndex: 98, position: 'sticky' }}>
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate" style={{ maxWidth: '150px' }} title={pkg.name}>
                                      {pkg.name}
                                  </div>
                                  {hasAddendums && (
                                    <div className="text-xs text-gray-500 font-medium truncate">
                                      ({addendums.length} addendum{addendums.length > 1 ? 's' : ''})
                                    </div>
                                  )}
                              </div>
                            </TableCell>
                              </>
                            )}
                       {visibleColumns.status && (
                         <TableCell className="text-center w-[120px] sticky left-[332px] bg-white sticky-left-column" style={{ backgroundColor: '#ffffff', opacity: 1, width: '120px', minWidth: '120px', maxWidth: '120px', zIndex: 97, position: 'sticky' }}>
                        <div className="flex items-center justify-center gap-2">
                          {(() => {
                            const status = pkg.submissionStatus;
                            const config = submissionStatusConfig[status] || { label: status, icon: Circle, color: 'bg-gray-50 text-gray-800' };
                            const IconComponent = config.icon;
                            
                            // Check if package is locked (read-only) - all statuses except draft should show lock
                            const isLocked = pkg.isLocked || pkg.submissionStatus !== 'draft' && pkg.submissionStatus !== 'not_sent';
                            
                            return (
                              <>
                                {isLocked && (
                                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${config.color}`}>
                                    <Lock className="h-3 w-3" />
                                    <span>{config.label}</span>
                                  </div>
                                )}
                                {!isLocked && (
                                  <>
                                    <IconComponent className="h-4 w-4" />
                                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
                                      {config.label}
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.files && (
                        <TableCell className="text-center w-[100px]">
                        <div className="flex items-center justify-center space-x-1 text-sm text-gray-600">
                          <FileText className="h-3 w-3" />
                          <span>{pkg.totalFiles || 0}</span>
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.type && (
                        <TableCell className="text-center w-[120px]">
                          <span className="text-xs font-medium text-gray-700">
                            <span className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-transparent">
                              {pkg.versionType === 'fixed' ? 'Fixed' : 'Current'}
                            </span>
                          </span>
                      </TableCell>
                      )}
                      {visibleColumns.createdAt && (
                        <TableCell className="text-center w-[150px]">
                        <span className="text-sm text-gray-600 truncate block">
                          {formatDate(pkg.createdAt)}
                        </span>
                      </TableCell>
                      )}
                      {visibleColumns.createdBy && (
                        <TableCell className="text-center w-[150px]">
                          <span 
                            className="text-sm text-gray-600 truncate block cursor-help" 
                            style={{ maxWidth: '130px' }}
                            title={pkg.createdBy}
                          >
                          {pkg.createdBy}
                        </span>
                      </TableCell>
                      )}
                      {visibleColumns.sentAt && (
                        <TableCell className="text-center w-[120px]">
                        <span className="text-sm text-gray-600">
                          {pkg.sentAt ? formatDate(pkg.sentAt) : '-'}
                        </span>
                      </TableCell>
                      )}
                      {visibleColumns.deadline && (
                        <TableCell className="text-center w-[120px]">
                        <span className="text-sm text-gray-600">
                          {pkg.submissionDeadline ? formatDate(pkg.submissionDeadline) : '-'}
                        </span>
                      </TableCell>
                      )}
                      {visibleColumns.companySent && (
                        <TableCell className="text-center w-[140px]">
                        <div className="text-sm font-medium text-gray-900">
                          {pkg.companySent || 0}
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.companyViewed && (
                        <TableCell className="text-center w-[140px]">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCompanyStatus(pkg.companyViewed || 0, getTotalCompanies(pkg))}
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.companyDownloaded && (
                        <TableCell className="text-center w-[140px]">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCompanyStatus(pkg.companyDownloaded || 0, getTotalCompanies(pkg))}
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.companyReturned && (
                        <TableCell className="text-center w-[150px]">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCompanyStatus(pkg.companyReturned || 0, getTotalCompanies(pkg))}
                        </div>
                      </TableCell>
                      )}
                      {visibleColumns.companyAwarded && (
                        <TableCell className="text-center w-[150px]">
                        <Badge variant={pkg.companyAwarded ? "default" : "outline"} className="text-xs">
                          {pkg.companyAwarded ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      )}
                    </TableRow>

                          {/* Addendum Rows (when expanded) */}
                          {isExpanded && hasAddendums && addendums.map((addendum) => (
                            <TableRow 
                              key={addendum.id}
                              className="cursor-pointer hover:bg-gray-200 border-b border-gray-100 bg-gray-50 h-10"
                              onClick={() => {
                                // Navigate directly to the addendum package using its ID
                                setLocation(`/tender-packages/projects/${projectId}/${addendum.id}`);
                              }}
                            >
                              {/* Selection checkbox for addendum */}
                              <TableCell className="w-[32px] sticky left-0 bg-gray-50 text-center" style={{ backgroundColor: '#f9fafb', opacity: 1, width: '32px', minWidth: '32px', maxWidth: '32px', zIndex: 100, position: 'sticky' }}>
                                <Checkbox
                                  checked={selectedItems.has(addendum.id)}
                                  onCheckedChange={checked => handleItemSelect(addendum.id, checked as boolean)}
                                  onClick={e => e.stopPropagation()}
                                  className="rounded-none h-3 w-3"
                                  style={{ verticalAlign: 'middle' }}
                                />
                              </TableCell>
                              {visibleColumns.package && (
                                <TableCell className="w-[140px] sticky left-[32px] bg-gray-50" style={{ backgroundColor: '#f9fafb', opacity: 1, width: '140px', minWidth: '140px', maxWidth: '140px', zIndex: 99, position: 'sticky' }}>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-5 h-5"></div>
                                  <Archive className="h-4 w-4 text-blue-500" />
                                    <div className="min-w-0">
                                      <div className="text-xs font-medium text-gray-900 truncate" style={{ maxWidth: '80px' }} title={pkg.packageNumber || `#${pkg.id.slice(-4)}`}>
                                        {pkg.packageNumber || `#${pkg.id.slice(-4)}`}
                                      </div>
                                      <div className="text-xs text-blue-600 font-medium">
                                        {addendum.packageNumber?.split('-').slice(1).join('-') || `-AD${addendums.findIndex(a => a.id === addendum.id) + 1}`}
                                      </div>
                                  </div>
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.package && (
                                 <TableCell className="w-[160px] sticky left-[172px] bg-gray-50" style={{ backgroundColor: '#f9fafb', opacity: 1, width: '160px', minWidth: '160px', maxWidth: '160px', zIndex: 98, position: 'sticky' }}>
                                  <div className="min-w-0">
                                    <div className="text-xs font-medium text-gray-900 truncate" style={{ maxWidth: '150px' }} title={addendum.name}>
                                      {addendum.name}
                                    </div>
                                  </div>
                                </TableCell>
                              )}
                               {visibleColumns.status && (
                                 <TableCell className="text-center w-[120px] sticky left-[332px] bg-gray-50 sticky-left-column" style={{ backgroundColor: '#f9fafb', opacity: 1, width: '120px', minWidth: '120px', maxWidth: '120px', zIndex: 97, position: 'sticky' }}>
                                <div className="flex items-center justify-center gap-2">
                                  {(() => {
                                    const status = addendum.submissionStatus;
                                    const config = submissionStatusConfig[status] || { label: status, icon: Circle, color: 'bg-gray-50 text-gray-800' };
                                    const IconComponent = config.icon;
                                    
                                    // Check if addendum is locked (read-only) - all statuses except draft should show lock
                                    const isLocked = addendum.isLocked || addendum.submissionStatus !== 'draft' && addendum.submissionStatus !== 'not_sent';
                                    
                                    return (
                                      <>
                                        {isLocked && (
                                          <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${config.color}`}>
                                            <Lock className="h-3 w-3" />
                                            <span className="text-xs">{config.label}</span>
                                          </div>
                                        )}
                                        {!isLocked && (
                                          <>
                                            <IconComponent className="h-3 w-3" />
                                            <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${config.color}`}>
                                              {config.label}
                                            </div>
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.files && (
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center space-x-1 text-xs text-gray-600">
                                  <FileText className="h-3 w-3" />
                                  <span>{addendum.totalFiles || 0}</span>
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.type && (
                                <TableCell className="text-center w-[120px]">
                                  <span className="text-xs font-medium text-gray-700">
                                    <span className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-transparent">
                                      {addendum.versionType === 'fixed' ? 'Fixed' : 'Current'}
                                    </span>
                                  </span>
                              </TableCell>
                              )}
                              {visibleColumns.createdAt && (
                                <TableCell className="text-center w-[150px]">
                                <span className="text-xs text-gray-600">
                                  {formatDate(addendum.createdAt)}
                                </span>
                              </TableCell>
                              )}
                              {visibleColumns.createdBy && (
                                <TableCell className="text-center w-[150px]">
                                  <span 
                                    className="text-xs text-gray-600 truncate block cursor-help" 
                                    style={{ maxWidth: '130px' }}
                                    title={addendum.createdBy}
                                  >
                                  {addendum.createdBy}
                                </span>
                              </TableCell>
                              )}
                              {visibleColumns.sentAt && (
                                <TableCell className="text-center w-[120px]">
                                <span className="text-xs text-gray-600">
                                  {addendum.sentAt ? formatDate(addendum.sentAt) : '-'}
                                </span>
                              </TableCell>
                              )}
                              {visibleColumns.deadline && (
                                <TableCell className="text-center w-[120px]">
                                <span className="text-xs text-gray-600">
                                  {addendum.submissionDeadline ? formatDate(addendum.submissionDeadline) : '-'}
                                </span>
                              </TableCell>
                              )}
                              {visibleColumns.companySent && (
                                <TableCell className="text-center w-[140px]">
                                <div className="text-xs font-medium text-gray-900">
                                  {addendum.companySent || 0}
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.companyViewed && (
                                <TableCell className="text-center w-[140px]">
                                <div className="text-xs font-medium text-gray-900">
                                  {formatCompanyStatus(addendum.companyViewed || 0, getTotalCompanies(addendum))}
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.companyDownloaded && (
                                <TableCell className="text-center w-[140px]">
                                <div className="text-xs font-medium text-gray-900">
                                  {formatCompanyStatus(addendum.companyDownloaded || 0, getTotalCompanies(addendum))}
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.companyReturned && (
                                <TableCell className="text-center w-[150px]">
                                <div className="text-xs font-medium text-gray-900">
                                  {formatCompanyStatus(addendum.companyReturned || 0, getTotalCompanies(addendum))}
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.companyAwarded && (
                                <TableCell className="text-center w-[150px]">
                                <Badge variant={addendum.companyAwarded ? "default" : "outline"} className="text-xs">
                                  {addendum.companyAwarded ? "Yes" : "No"}
                                </Badge>
                              </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

          </div>
        )}
      </main>
      
      {/* Fixed Pagination at Screen Bottom */}
      {filteredPackages.length > 0 && (
        <div className="flex-shrink-0">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            className="border-0"
            viewMode={viewMode}
          />
        </div>
      )}

      {/* Share Link Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-blue-600" />
              Share Package Download Link
            </DialogTitle>
            <DialogDescription>
              Share this link to allow others to download the package. The link expires in 24 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Input
                id="share-link"
                value={shareUrl}
                readOnly
                className="font-mono text-sm"
              />
            </div>
            <Button size="sm" className="px-3" onClick={copyToClipboard}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={copyToClipboard}>
              <Copy className="mr-2 h-4 w-4" />
              Copy Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
